import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import {
  DirectorSearchBoxComponent,
  DirectorSearchFieldsConfig
} from '../../directors/director-search-box/director-search-box.component';
import { DirectorSearchFilter } from '../../../models/directorSearchFilter';
import { DirectorFlat } from '../../../models/director';
import { ColumnDefinition } from '../../../models/columnDefinition';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { DirectorService } from '../../../services/director.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { IAppState } from '../../../store/states/app.state';
import {
  SetPortalDirectorsSearchFilter,
  SetPortalDirectorsPagination,
  SetPortalDirectorsSort
} from '../../../store/actions/portal-directors-list.actions';
import { selectPortalDirectorsListFullState } from '../../../store/selectors/portal-directors-list.selectors';

@Component({
  selector: 'app-portal-directors',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    DirectorSearchBoxComponent,
    GenericListComponent,
    NgxSpinnerModule
  ],
  templateUrl: './portal-directors.component.html',
  styleUrls: ['./portal-directors.component.scss']
})
export class PortalDirectorsComponent {
  @ViewChild(GenericListComponent) genericList?: GenericListComponent<DirectorFlat>;

  directors: DirectorFlat[] = [];
  totalItems = 0;
  isSearchVisible = true;

  currentPage = 1;
  pageSize = 10;
  sortBy = 'fullName';
  ascending = true;
  currentSearchFilter: DirectorSearchFilter = {};

  showBoardMemberships = false;

  searchFieldsConfig: DirectorSearchFieldsConfig = {
    showFullName: true,
    showAgeFrom: true,
    showAgeTo: true,
    showGender: true,
    showNationality: true,
    showProfession: true,
    showRegion: true,
    showCountry: true,
    showYear: true,
    showCompany: true,
    showBoardMemberOccupation: true,
    showClassification: true,
    showIndustry: true,
    showMembershipType: true,
    showTenure: true,
    showAdminTab: false
  };

  private allColumnDefinitions: ColumnDefinition[] = [
    { propertyName: 'fullName', displayName: 'Full Name' },
    { propertyName: 'genderDisplay', displayName: 'Gender' },
    { propertyName: 'nationalityName', displayName: 'Nationality' },
    { propertyName: 'boardMembershipDisplay', displayName: 'Board Memberships', dataType: 'list', sortable: false },
    { propertyName: 'isDeceased', displayName: 'Passed Away', dataType: 'boolean' },
    { propertyName: 'computedAge', displayName: 'Age', sortable: false },
    { propertyName: 'professionName', displayName: 'Profession' }
  ];

  get columnDefinitions(): ColumnDefinition[] {
    if (this.showBoardMemberships) {
      return this.allColumnDefinitions;
    }
    return this.allColumnDefinitions.filter(c => c.propertyName !== 'boardMembershipDisplay');
  }

  constructor(
    private directorService: DirectorService,
    private toast: HotToastService,
    private spinnerService: NgxSpinnerService,
    private lookupDataService: LookupDataService,
    private router: Router,
    private store: Store<IAppState>
  ) {}

  onViewDirector(director: DirectorFlat): void {
    this.router.navigate(['/portal/directors', director.id]);
  }

  back(): void {
    this.router.navigate(['/portal']);
  }

  ngOnInit(): void {
    this.lookupDataService.loadCustomerDirectorLookups().catch(error => {
      console.error('Error loading director lookups:', error);
      this.toast.error('Error loading filters');
    });

    // Restore persisted filter state so the search box remembers the user's
    // previous selections, but don't auto-run the search — the user must
    // explicitly trigger it from the search box.
    this.store.select(selectPortalDirectorsListFullState).pipe(
      filter(state => state !== undefined && state !== null),
      take(1)
    ).subscribe(state => {
      this.currentSearchFilter = state.searchFilter;
      this.currentPage = state.currentPage;
      this.pageSize = state.pageSize;
      this.sortBy = state.sortBy;
      this.ascending = state.ascending;
    });
  }

  private loadDirectors(): void {
    this.spinnerService.show();

    const searchPayload = {
      ...this.currentSearchFilter,
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending
    };

    this.directorService.customerSearchDirectors(searchPayload).subscribe({
      next: (response) => {
        this.directors = response.data.map(director => ({
          ...director,
          boardMembershipDisplay: (director.boardMemberships || [])
            .sort((a, b) => a.companyName.localeCompare(b.companyName) || a.year - b.year)
            .map(m => ({ label: `${m.companyName} (${m.year})`, companyId: m.companyId, year: m.year })),
          computedAge: this.computeAge(director)
        }));
        this.totalItems = response.totalCount;
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading directors:', error);
        this.toast.error('Error loading directors');
        this.spinnerService.hide();
      }
    });
  }

  // Column propertyNames that don't match a backend sort key. The list emits the
  // propertyName as sort.active, so map to the actual entity column before the request.
  private readonly sortKeyMap: Record<string, string> = {
    genderDisplay: 'gender'
  };

  onSortChange(sort: Sort): void {
    if (!sort.direction) {
      // User cleared the sort — fall back to the default (full name, ascending).
      this.sortBy = 'fullName';
      this.ascending = true;
    } else {
      this.sortBy = this.sortKeyMap[sort.active] ?? sort.active;
      this.ascending = sort.direction === 'asc';
    }

    this.store.dispatch(new SetPortalDirectorsSort({
      sortBy: this.sortBy,
      ascending: this.ascending
    }));

    this.loadDirectors();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;

    this.store.dispatch(new SetPortalDirectorsPagination({
      currentPage: this.currentPage,
      pageSize: this.pageSize
    }));

    this.loadDirectors();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.currentSearchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  // Age is yearOfBirth-derived. If the director has passed away, freeze at the death year;
  // otherwise it's current-year minus year of birth. Mirrors the form's reference-year logic.
  private computeAge(director: DirectorFlat): number | null {
    if (!director.yearOfBirth) return null;
    if (director.isDeceased && director.dateOfDeath) {
      const deathDate = director.dateOfDeath instanceof Date ? director.dateOfDeath : new Date(director.dateOfDeath);
      if (!isNaN(deathDate.getTime())) {
        return deathDate.getFullYear() - director.yearOfBirth;
      }
    }
    return new Date().getFullYear() - director.yearOfBirth;
  }

  onDirectorSearchFilterChange(directorSearchFilter: DirectorSearchFilter): void {
    this.currentSearchFilter = directorSearchFilter;
    this.currentPage = 1;

    // The search box's Clear button emits an empty filter — treat that as a full reset
    // back to the default sort so the list lands exactly where the user started.
    const isCleared = Object.values(directorSearchFilter).every(v => v === undefined || v === null || v === '');
    if (isCleared) {
      this.sortBy = 'fullName';
      this.ascending = true;
      this.store.dispatch(new SetPortalDirectorsSort({ sortBy: this.sortBy, ascending: this.ascending }));
    }

    this.store.dispatch(new SetPortalDirectorsSearchFilter(directorSearchFilter));

    if (this.genericList?.paginator) {
      this.genericList.paginator.pageIndex = 0;
    }

    this.loadDirectors();
  }
}
